import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";
import { Lightning, ArrowLeft } from "@phosphor-icons/react";
import { Seo } from "@/components/Seo";

const LegalShell = ({ title, children }) => {
  const { lang, setLang } = useT();
  const isPrivacy = window.location.pathname.includes("privacy");
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Seo
        title={`${title} — TradeMind AI`}
        description={isPrivacy
          ? "Zásady ochrany osobních údajů TradeMind AI — jak zpracováváme data traderů v souladu s GDPR. Provozovatel Vratislav Výšek (VV Software), IČO: 30038758."
          : "Obchodní podmínky TradeMind AI — pravidla používání AI asistenta pro tradery, předplatné PREMIUM a ELITE, právo na odstoupení. Provozovatel Vratislav Výšek (VV Software)."}
      />
      <header className="border-b border-zinc-800 sticky top-0 z-10 bg-zinc-950/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-8 h-8 rounded-md" />
            <span className="font-semibold tracking-tight text-zinc-50">TradeMind <span className="text-blue-500">AI</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider border border-zinc-800 rounded-md overflow-hidden">
              <button onClick={() => setLang("cs")} className={`px-2 py-1 ${lang === "cs" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500"}`}>CS</button>
              <button onClick={() => setLang("en")} className={`px-2 py-1 ${lang === "en" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500"}`}>EN</button>
            </div>
            <Link to="/" className="text-sm text-zinc-400 hover:text-zinc-100 flex items-center gap-1"><ArrowLeft size={14} weight="bold" /> {lang === "cs" ? "Zpět" : "Back"}</Link>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl tracking-tight font-medium text-zinc-50 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h1>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-10">
          {lang === "cs" ? "Poslední aktualizace" : "Last updated"}: 12. 2. 2026
        </p>
        <div className="prose prose-invert max-w-none">
          {children}
        </div>
      </main>
    </div>
  );
};

const H2 = ({ children }) => <h2 className="text-xl font-medium tracking-tight text-zinc-100 mt-8 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{children}</h2>;
const P = ({ children }) => <p className="text-sm text-zinc-300 leading-relaxed mb-3">{children}</p>;
const UL = ({ children }) => <ul className="text-sm text-zinc-300 space-y-1.5 mb-3 list-disc list-inside marker:text-blue-500">{children}</ul>;

export const Privacy = () => {
  const { lang } = useT();
  if (lang === "cs") {
    return (
      <LegalShell title="Zásady ochrany osobních údajů">
        <P>Tyto zásady popisují, jak TradeMind AI (provozovatel: <strong>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno</strong>, kontakt: <a href="mailto:vvsoftware@seznam.cz" className="text-blue-400">vvsoftware@seznam.cz</a>) zpracovává tvoje osobní údaje v souladu s nařízením GDPR.</P>

        <H2>1. Jaká data sbíráme</H2>
        <UL>
          <li><strong>Identifikační údaje:</strong> email a jméno (registrace)</li>
          <li><strong>Obsah od uživatele:</strong> screenshoty grafů, poznámky k obchodům, údaje o prop firm účtech</li>
          <li><strong>Provozní data:</strong> IP adresa, typ zařízení, čas přístupu (pro bezpečnost)</li>
          <li><strong>Platební údaje:</strong> NE — zpracovává Stripe, my máme jen ID transakce a status</li>
        </UL>

        <H2>2. Proč to zpracováváme</H2>
        <UL>
          <li>Poskytování služby (AI analýza, deník, mentor)</li>
          <li>Správa předplatného a fakturace</li>
          <li>Bezpečnost účtu (přihlašování, ochrana proti zneužití)</li>
          <li>Zlepšování AI modelů (anonymizované)</li>
        </UL>

        <H2>3. Komu data předáváme</H2>
        <UL>
          <li><strong>OpenAI</strong> — screenshoty grafů jsou odesílány k analýze přes GPT-5.2. OpenAI je nesmí použít k trénování modelů (Enterprise smlouva).</li>
          <li><strong>Stripe</strong> — platební údaje pro zpracování plateb</li>
          <li><strong>MongoDB cloud</strong> — uložení dat (EU region)</li>
          <li><strong>Email služba</strong> — pouze pro transakční emaily (potvrzení, reset hesla)</li>
        </UL>
        <P>Data NEPRODÁVÁME třetím stranám. Nepoužíváme reklamní trackery.</P>

        <H2>4. Jak dlouho data uchováváme</H2>
        <UL>
          <li>Aktivní účet: po celou dobu trvání</li>
          <li>Zrušený účet: 30 dní (možnost obnovy), pak smazáno</li>
          <li>Účetní záznamy o platbách: 10 let (zákonná povinnost)</li>
        </UL>

        <H2>5. Tvoje práva (GDPR)</H2>
        <UL>
          <li>Právo na přístup k tvým datům</li>
          <li>Právo na opravu</li>
          <li>Právo na výmaz (právo být zapomenut)</li>
          <li>Právo na přenositelnost</li>
          <li>Právo odvolat souhlas</li>
          <li>Právo podat stížnost na Úřad pro ochranu osobních údajů (uoou.cz)</li>
        </UL>
        <P>Pro uplatnění práv napiš na <a href="mailto:vvsoftware@seznam.cz" className="text-blue-400">vvsoftware@seznam.cz</a>. Odpovíme do 30 dní.</P>

        <H2>6. Bezpečnost</H2>
        <P>Hesla jsou hashována (bcrypt). Komunikace probíhá výhradně přes HTTPS. Tokeny mají expiraci 30 dní. Pravidelně auditujeme přístupy do databáze.</P>

        <H2>7. Cookies</H2>
        <P>Používáme pouze nezbytné cookies pro fungování aplikace (autentizace, jazyk). Nepoužíváme analytické ani reklamní cookies bez tvého souhlasu.</P>

        <H2>8. Disclaimer</H2>
        <P><strong>TradeMind AI poskytuje analytické nástroje, NIKOLI investiční poradenství.</strong> Veškeré obchodní rozhodnutí jsou na tvojí odpovědnosti. Obchodování s pákovými produkty (Forex, CFD, krypto) nese vysoké riziko ztráty kapitálu.</P>

        <H2>9. Změny zásad</H2>
        <P>Pokud zásady významně změníme, oznámíme to emailem s alespoň 14denním předstihem.</P>

        <H2>10. Kontakt</H2>
        <P>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno, vvsoftware@seznam.cz, tel. +420 702 995 353. Fyzická osoba zapsaná v živnostenském rejstříku. Neplátce DPH. Pověřenec pro ochranu osobních údajů: vvsoftware@seznam.cz.</P>
      </LegalShell>
    );
  }

  return (
    <LegalShell title="Privacy Policy">
      <P>This policy explains how TradeMind AI (operator: <strong>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno</strong>, contact: <a href="mailto:vvsoftware@seznam.cz" className="text-blue-400">vvsoftware@seznam.cz</a>) processes your personal data in accordance with GDPR.</P>

      <H2>1. What data we collect</H2>
      <UL>
        <li><strong>Identity:</strong> email and name (registration)</li>
        <li><strong>User content:</strong> chart screenshots, trade notes, prop firm account data</li>
        <li><strong>Operational data:</strong> IP address, device type, access time (for security)</li>
        <li><strong>Payment data:</strong> NO — handled by Stripe, we only have transaction ID and status</li>
      </UL>

      <H2>2. Why we process it</H2>
      <UL>
        <li>Providing the service (AI analysis, journal, mentor)</li>
        <li>Managing subscriptions and billing</li>
        <li>Account security</li>
        <li>Improving AI models (anonymized)</li>
      </UL>

      <H2>3. Who we share data with</H2>
      <UL>
        <li><strong>OpenAI</strong> — chart screenshots are sent for analysis via GPT-5.2. Under enterprise agreement they cannot use it for model training.</li>
        <li><strong>Stripe</strong> — payment data for payment processing</li>
        <li><strong>MongoDB cloud</strong> — data storage (EU region)</li>
        <li><strong>Email provider</strong> — transactional emails only</li>
      </UL>
      <P>We do NOT sell data to third parties. We do not use advertising trackers.</P>

      <H2>4. Data retention</H2>
      <UL>
        <li>Active account: full duration</li>
        <li>Canceled account: 30 days (recovery), then deleted</li>
        <li>Billing records: 10 years (legal requirement)</li>
      </UL>

      <H2>5. Your rights (GDPR)</H2>
      <UL>
        <li>Right to access your data</li>
        <li>Right to rectification</li>
        <li>Right to erasure</li>
        <li>Right to data portability</li>
        <li>Right to withdraw consent</li>
        <li>Right to lodge a complaint with the supervisory authority</li>
      </UL>
      <P>Email <a href="mailto:vvsoftware@seznam.cz" className="text-blue-400">vvsoftware@seznam.cz</a>. We respond within 30 days.</P>

      <H2>6. Security</H2>
      <P>Passwords are hashed (bcrypt). All communication via HTTPS. Tokens expire after 30 days.</P>

      <H2>7. Cookies</H2>
      <P>We only use essential cookies (authentication, language). No analytics or advertising cookies without your consent.</P>

      <H2>8. Disclaimer</H2>
      <P><strong>TradeMind AI provides analytical tools, NOT investment advice.</strong> All trading decisions are your responsibility. Leveraged trading carries a high risk of capital loss.</P>

      <H2>9. Changes</H2>
      <P>Material changes will be announced via email with at least 14 days notice.</P>

      <H2>10. Contact</H2>
      <P>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno, vvsoftware@seznam.cz, phone +420 702 995 353. Registered sole trader in the Czech Trade Register. Not a VAT payer. DPO: vvsoftware@seznam.cz.</P>
    </LegalShell>
  );
};

export const Terms = () => {
  const { lang } = useT();
  if (lang === "cs") {
    return (
      <LegalShell title="Obchodní podmínky">
        <P>Tyto podmínky upravují používání služby TradeMind AI provozované společností <strong>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno</strong></P>

        <H2>1. Definice</H2>
        <P><strong>Služba</strong> = webová a mobilní aplikace TradeMind AI a všechny její funkce. <strong>Uživatel</strong> = osoba registrovaná ve službě. <strong>Předplatné</strong> = placený plán PREMIUM nebo ELITE.</P>

        <H2>2. Registrace</H2>
        <P>Pro využívání služby je nutné vytvořit účet s pravdivým emailem. Účet je osobní a nepřenosný. Heslo si chraň — neneseme odpovědnost za ztráty způsobené prozrazením přihlašovacích údajů.</P>

        <H2>3. Předplatné a platby</H2>
        <UL>
          <li>FREE plán: zdarma, omezeno na 3 AI analýzy denně</li>
          <li>PREMIUM plán: <strong>499 Kč / měsíc</strong> (vč. DPH)</li>
          <li>ELITE plán: <strong>1 290 Kč / měsíc</strong> (vč. DPH)</li>
          <li>Platby na webu zpracovává Stripe (akceptujeme platební karty); v aplikaci z Google Play probíhají platby přes Google Play.</li>
          <li>Předplatné se automaticky obnovuje vždy o měsíc.</li>
          <li>Můžeš ho zrušit kdykoli v Nastavení — zůstává aktivní do konce zaplaceného období.</li>
        </UL>

        <H2>4. Odstoupení od smlouvy</H2>
        <P>Jako spotřebitel máš právo odstoupit od smlouvy do 14 dnů od první platby BEZ udání důvodu. Stačí napsat na <a href="mailto:vvsoftware@seznam.cz" className="text-blue-400">vvsoftware@seznam.cz</a>. <strong>Výjimka:</strong> Pokud již službu aktivně využíváš (např. provedl jsi více než 10 AI analýz), souhlasíš se zahájením plnění a ztrácíš právo na odstoupení v rozsahu skutečně vyčerpaných služeb.</P>

        <H2>5. Zakázané chování</H2>
        <UL>
          <li>Sdílení účtu s třetími osobami</li>
          <li>Pokusy o reverse engineering, scraping nebo obcházení limitů</li>
          <li>Nahrávání nezákonného obsahu</li>
          <li>Používání služby k nezákonnému obchodování</li>
        </UL>
        <P>Při porušení můžeme účet bez náhrady zrušit.</P>

        <H2>6. Disclaimer — KRITICKÉ</H2>
        <P><strong>TradeMind AI NENÍ investiční poradce, broker ani regulovaná finanční instituce.</strong> Veškerá data, analýzy, signály a doporučení slouží <strong>výhradně ke vzdělávacím a analytickým účelům</strong>. Nejsou investičním poradenstvím dle zákona č. 256/2004 Sb.</P>
        <P>Obchodování s pákovými produkty (Forex, CFD, kryptoměny) nese <strong>extrémně vysoké riziko ztráty kapitálu</strong>. Můžeš přijít o všechny vložené prostředky, případně i o víc. Obchoduj pouze s penězi, jejichž ztrátu si můžeš dovolit.</P>
        <P>AI analýzy mohou obsahovat chyby. Výsledky z minulosti negarantují budoucí výsledky. Veškerá rozhodnutí jsou výhradně na tvojí odpovědnosti.</P>

        <H2>7. Omezení odpovědnosti</H2>
        <P>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno neodpovídá za žádné přímé ani nepřímé škody, ušlý zisk, ztrátu dat nebo obchodní ztráty vzniklé z používání služby. Naše celková odpovědnost je omezena na výši zaplaceného předplatného za posledních 12 měsíců.</P>

        <H2>8. Duševní vlastnictví</H2>
        <P>Veškerý kód, design a obsah služby je vlastnictvím Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno Tvůj uživatelský obsah (screenshoty, poznámky) zůstává tvým majetkem — udělíš nám pouze licenci jej použít pro poskytování služby a anonymizované zlepšování AI.</P>

        <H2>9. Ukončení</H2>
        <P>Můžeš účet kdykoli zrušit. My můžeme účet zrušit s 30denní výpovědní lhůtou nebo okamžitě při porušení podmínek.</P>

        <H2>10. Rozhodné právo</H2>
        <P>Tyto podmínky se řídí právem České republiky. Případné spory řeší obecné soudy ČR.</P>

        <H2>11. Kontakt</H2>
        <P>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno, vvsoftware@seznam.cz, tel. +420 702 995 353. Fyzická osoba zapsaná v živnostenském rejstříku. Neplátce DPH.</P>
      </LegalShell>
    );
  }

  return (
    <LegalShell title="Terms of Service">
      <P>These Terms govern your use of TradeMind AI operated by <strong>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno</strong></P>

      <H2>1. Definitions</H2>
      <P><strong>Service</strong> = TradeMind AI web and mobile app. <strong>User</strong> = registered account holder. <strong>Subscription</strong> = paid PREMIUM or ELITE plan.</P>

      <H2>2. Registration</H2>
      <P>You must create an account with a valid email. Accounts are personal and non-transferable.</P>

      <H2>3. Subscription &amp; Payments</H2>
      <UL>
        <li>FREE plan: free, limited to 3 AI analyses per day</li>
        <li>PREMIUM plan: <strong>499 CZK / month</strong> (VAT incl.)</li>
        <li>ELITE plan: <strong>1290 CZK / month</strong> (VAT incl.)</li>
        <li>Web payments processed by Stripe (cards accepted); Google Play app purchases are handled by Google Play billing.</li>
        <li>Subscription auto-renews monthly.</li>
        <li>Cancel anytime in Settings — remains active until end of paid period.</li>
      </UL>

      <H2>4. Refunds / Withdrawal</H2>
      <P>EU consumers have a 14-day right of withdrawal. Email <a href="mailto:vvsoftware@seznam.cz" className="text-blue-400">vvsoftware@seznam.cz</a>. Exception: if you actively use the service (10+ AI analyses), you waive withdrawal for consumed services.</P>

      <H2>5. Prohibited Conduct</H2>
      <UL>
        <li>Sharing accounts</li>
        <li>Reverse engineering, scraping, bypassing limits</li>
        <li>Uploading illegal content</li>
        <li>Using the service for illegal trading</li>
      </UL>

      <H2>6. Disclaimer — CRITICAL</H2>
      <P><strong>TradeMind AI is NOT an investment advisor, broker, or regulated financial institution.</strong> All data, analyses, signals and recommendations are for <strong>educational and analytical purposes only</strong>.</P>
      <P>Trading leveraged products (Forex, CFDs, crypto) carries an <strong>extremely high risk of capital loss</strong>. Only trade with money you can afford to lose. Past results do not guarantee future performance.</P>

      <H2>7. Limitation of Liability</H2>
      <P>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno is not liable for any direct or indirect damages, lost profits, data loss or trading losses arising from use of the service. Our total liability is limited to the subscription fees paid in the last 12 months.</P>

      <H2>8. Intellectual Property</H2>
      <P>All code, design and content is the property of Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno Your user content remains yours — you grant us a license to use it to provide the service and improve our AI (anonymized).</P>

      <H2>9. Termination</H2>
      <P>You may cancel at any time. We may terminate with 30-day notice or immediately for breach.</P>

      <H2>10. Governing Law</H2>
      <P>These Terms are governed by the laws of the Czech Republic.</P>

      <H2>11. Contact</H2>
      <P>Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno, vvsoftware@seznam.cz, phone +420 702 995 353. Registered sole trader in the Czech Trade Register. Not a VAT payer.</P>
    </LegalShell>
  );
};
